import React from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    ListItemButton,
    Stack,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {stripReportSaysPrefix} from '../../../../../utils/stripReportSaysPrefix';
import {formatSegmentsInDocumentsSummary} from './formatSegmentsInDocumentsSummary';

/** Levels 1–5: light green → green → yellow → orange → red */
const SIGNIFICANCE_BAR_COLORS = ['#c8e6c9', '#66bb6a', '#ffee58', '#ffa726', '#ef5350'];

function changeLineText(update) {
    const s = update?.summary?.trim();
    if (s) return s;
    const u = update?.sourceUpdate?.trim();
    if (u) return u.length > 220 ? `${u.slice(0, 217)}…` : u;
    return update?.name?.trim() || 'Update';
}

function isClaimMatchingPhase(stage) {
    return (
        stage === 'LOOKING_FOR_CLAIM_UPDATES'
        || stage === 'CLAIM_UPDATES_RETRIEVED'
        || stage === 'CLUSTERING'
        || stage === 'PARTIAL'
    );
}

function buildClaimsStatusMessage({
    claimDeltaStage,
    claimDeltaMessage,
    claimUpdateProgressPercent,
    segmentClaimBatchIndex,
    segmentClaimBatchTotal,
}) {
    if (claimDeltaMessage) {
        return claimDeltaMessage;
    }
    if (claimDeltaStage === 'SEGMENT_CLAIMS_PROGRESS'
        && segmentClaimBatchIndex != null
        && segmentClaimBatchTotal != null) {
        return `Analyzing corpus segments (batch ${segmentClaimBatchIndex}/${segmentClaimBatchTotal})`;
    }
    if (isClaimMatchingPhase(claimDeltaStage)) {
        const pct =
            claimUpdateProgressPercent != null && Number.isFinite(Number(claimUpdateProgressPercent))
                ? Math.round(Number(claimUpdateProgressPercent))
                : null;
        return pct != null ? `Matching claims to corpus (${pct}%)` : 'Matching claims to corpus';
    }
    if (claimDeltaStage === 'REPORT_CLAIMS_STARTED' || claimDeltaStage === 'REPORT_CLAIMS_DONE') {
        return 'Extracting claims from report';
    }
    if (claimDeltaStage === 'LOADING_SEGMENTS' || claimDeltaStage === 'LOADED_SEGMENTS') {
        return 'Loading corpus segments';
    }
    if (claimDeltaStage === 'CACHED') {
        return 'Loading cached results';
    }
    return 'Extracting claims';
}

function SignificanceMeter({level}) {
    const n = Math.min(5, Math.max(1, Math.round(Number(level))));
    return (
        <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{mt: 0.5}}
            role="img"
            aria-label={`Significance ${n} of 5`}
        >
            {[1, 2, 3, 4, 5].map((i) => (
                <Box
                    key={i}
                    sx={{
                        width: 28,
                        height: 8,
                        borderRadius: 0.5,
                        bgcolor: i <= n ? SIGNIFICANCE_BAR_COLORS[i - 1] : 'action.hover',
                        border: 1,
                        borderColor: 'divider',
                    }}
                />
            ))}
        </Stack>
    );
}

export default function ClaimDeltasView({
    isProductLoading = false,
    storiesLoading,
    storiesError,
    claimDeltaStage,
    claimDeltaMessage,
    claimUpdateProgressPercent,
    segmentClaimBatchIndex,
    segmentClaimBatchTotal,
    claimGroups,
    selectedClaimKeys,
    onClaimToggle,
    creatingPitch,
    onCreatePitch,
    createPitchError,
    createPitchSuccess,
    onOpenCreatedPitch,
    onCloseCreatePitchSuccess,
    onUpdateOpen,
}) {
    const [expandedKeys, setExpandedKeys] = React.useState(() => new Set());

    const toggleExpanded = (key, event) => {
        event?.stopPropagation();
        setExpandedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const showStatusRow = isProductLoading || (Boolean(storiesLoading) && !storiesError);
    let statusMessage = '';
    if (isProductLoading) {
        statusMessage = 'Waiting for product to load';
    } else if (storiesLoading && !storiesError) {
        statusMessage = buildClaimsStatusMessage({
            claimDeltaStage,
            claimDeltaMessage,
            claimUpdateProgressPercent,
            segmentClaimBatchIndex,
            segmentClaimBatchTotal,
        });
    }

    const showListWhileMatching =
        Boolean(storiesLoading) && !isProductLoading && !storiesError && claimGroups.length > 0;
    const showListComplete = !storiesLoading && !storiesError && claimGroups.length > 0;
    const showClaimList = showListWhileMatching || showListComplete;

    const renderClaimCards = () =>
        claimGroups.map((claim) => {
            const key = claim.claimKey;
            const expanded = expandedKeys.has(key);
            const rawHeadline =
                claim.reportClaim?.trim() || (claim.claimId ? `Claim ${claim.claimId}` : 'FINTEL claim');
            const headline = stripReportSaysPrefix(rawHeadline) || rawHeadline;
            const collapsedPreview = headline.length > 140 ? `${headline.slice(0, 137)}…` : headline;
            const citationRaw = (claim.reportCitation != null && String(claim.reportCitation).trim()
                ? String(claim.reportCitation).trim()
                : rawHeadline
            ).trim();

            const sigLevel =
                claim.maxSignificance != null && Number.isFinite(Number(claim.maxSignificance))
                    ? Math.min(5, Math.max(1, Math.round(Number(claim.maxSignificance))))
                    : null;

            return (
                <Box
                    key={key}
                    sx={{
                        position: 'relative',
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                    }}
                >
                    <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{p: 1.5, pr: 1}}>
                        <Checkbox
                            checked={selectedClaimKeys.has(String(key))}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(event) => onClaimToggle(claim, event.target.checked)}
                            sx={{mt: -0.5}}
                        />
                        <Box sx={{flex: 1, minWidth: 0}}>
                            <Typography variant="body2" fontWeight={400} color="text.primary">
                                {expanded ? headline : collapsedPreview}
                            </Typography>
                            {sigLevel != null ? (
                                <>
                                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.75}}>
                                        <Box component="span" sx={{fontWeight: 700}}>
                                            Significance
                                        </Box>
                                    </Typography>
                                    <SignificanceMeter level={sigLevel}/>
                                </>
                            ) : null}
                            <Stack direction="row" flexWrap="wrap" sx={{mt: 0.75, gap: 0.5}}>
                                {(claim.targetEntityLabels || []).map((label, i) => (
                                    <Chip key={`${label}-${i}`} size="small" label={label} variant="outlined"/>
                                ))}
                            </Stack>
                        </Box>
                        <IconButton
                            size="small"
                            aria-expanded={expanded}
                            aria-label={expanded ? 'Collapse claim' : 'Expand claim'}
                            onClick={(e) => toggleExpanded(key, e)}
                            sx={{
                                transform: expanded ? 'rotate(180deg)' : 'none',
                                transition: 'transform 0.2s',
                            }}
                        >
                            <ExpandMoreIcon/>
                        </IconButton>
                    </Stack>

                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{px: 1.5, pb: 1.5, pt: 0}}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{display: 'block'}}>
                                Citation
                            </Typography>
                            <Typography variant="body2" sx={{mt: 0.5, fontStyle: 'italic', color: 'text.secondary'}}>
                                &ldquo;{citationRaw || '—'}&rdquo;
                            </Typography>
                            <Stack spacing={1.25} sx={{mt: 1.5}}>
                                {claim.updates.map((update) => (
                                    <ListItemButton
                                        key={update.updateId}
                                        onClick={() => onUpdateOpen(claim, update)}
                                        alignItems="flex-start"
                                        sx={{
                                            flexDirection: 'column',
                                            alignItems: 'stretch',
                                            border: (theme) => `1px solid ${theme.palette.divider}`,
                                            borderRadius: 1,
                                            py: 1,
                                            px: 1.25,
                                        }}
                                    >
                                        <Typography variant="body2" color="text.primary">
                                            {changeLineText(update)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{mt: 0.5}}>
                                            {formatSegmentsInDocumentsSummary(update.segments)}
                                        </Typography>
                                    </ListItemButton>
                                ))}
                            </Stack>
                        </Box>
                    </Collapse>
                </Box>
            );
        });

    return (
        <Stack spacing={2} sx={{p: 2}}>
            {showStatusRow && (
                <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18}/>
                    <Typography variant="body2">{statusMessage}</Typography>
                </Stack>
            )}

            {storiesError && <Alert severity="error">{storiesError}</Alert>}

            {showClaimList && (
                <Stack spacing={1.5} sx={{pb: selectedClaimKeys.size > 0 ? 9 : 0}}>{renderClaimCards()}</Stack>
            )}

            {!storiesLoading && !storiesError && claimGroups.length === 0 && (
                <Typography variant="body2">No claims with corpus updates yet.</Typography>
            )}

            {selectedClaimKeys.size > 0 && (
                <Box
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        bgcolor: 'background.paper',
                        pt: 1,
                        mt: 'auto',
                        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                >
                    {createPitchSuccess ? (
                        <Alert severity="success">
                            <Stack>New pitch created.</Stack>
                            <Stack direction="row" spacing={1} pt={2}>
                                <Button size="small" onClick={onOpenCreatedPitch} variant="contained" color="success">
                                    Open in new tab
                                </Button>
                                <Button size="small" color="inherit" onClick={onCloseCreatePitchSuccess}>
                                    Close
                                </Button>
                            </Stack>
                        </Alert>
                    ) : (
                        <Stack spacing={1}>
                            {createPitchError && <Alert severity="error">{createPitchError}</Alert>}
                            <Button variant="contained" onClick={onCreatePitch} disabled={creatingPitch} fullWidth>
                                {creatingPitch ? 'Creating pitch...' : `Create new pitch (${selectedClaimKeys.size})`}
                            </Button>
                        </Stack>
                    )}
                </Box>
            )}
        </Stack>
    );
}
